import type { APIClient } from "@heidelbergmaterials/fcl-apiclient"
import {
	getSupportedLanguages,
	toggleSupportedLanguage,
	type SupportedLanguage,
} from "@shared/api"
import { create } from "zustand"
import { immer } from "zustand/middleware/immer"

type State = {
	languages: SupportedLanguage[] | undefined
}

type Actions = {
	fetchSupportedLanguages: (apiClient: APIClient) => Promise<void>
	toggleLanguage: (
		apiClient: APIClient,
		languageCode: string,
		active: boolean,
	) => Promise<void>
	resetStore: () => void
}

const initialState: State = {
	languages: undefined,
}

const findByCode = (
	languages: SupportedLanguage[] | undefined,
	languageCode: string,
) => languages?.find(l => l.languageCode === languageCode)

export const useSupportedLanguagesStore = create<State & Actions>()(
	immer((set, get) => ({
		...initialState,

		fetchSupportedLanguages: async apiClient => {
			const languages = await getSupportedLanguages(apiClient)

			set(state => {
				state.languages = languages
			})
		},

		toggleLanguage: async (apiClient, languageCode, active) => {
			const setActive = (value: boolean) =>
				set(state => {
					const language = findByCode(state.languages, languageCode)

					if (language) {
						language.active = value
					}
				})

			const previousActive = findByCode(
				get().languages,
				languageCode,
			)?.active

			// Optimistically reflect the change immediately so the UI does not
			// wait for the API round-trip.
			setActive(active)

			try {
				await toggleSupportedLanguage(apiClient, languageCode, active)
			} catch (error) {
				// Revert the optimistic update if the request fails.
				if (previousActive !== undefined) {
					setActive(previousActive)
				}

				throw error
			}
		},

		resetStore: () => set(initialState),
	})),
)
